import React, { useContext, useEffect, useMemo, useState } from "react";
import "./BranchesFaults.css";
import { Context } from "../../context/Context";
import { formatter, monthNames } from "../../utils/helpers";
import { translations } from "../../utils/lang";
import { COMP_FIELDS2 } from "../../utils/Compensation";
import { fetchBranchesCompsAPI } from "../../utils/api/report";
import { ExpandBranchesFaults } from "./expandBranchesFaults/ExpandBranchesFaults";
import { getObjectionsIdsAPI } from "../../utils/api/objection";
import { ArrowDown, ArrowUp, Button, Input } from "../ui";
import FilterGroup from "../analysis/filter/FilterUI";

const normalize = (val) =>
  String(val ?? "")
    .trim()
    .toLowerCase();

const toNumber = (value) => Number.parseFloat(value || 0) || 0;

export const FaultSummaryCard = ({
  className = "",
  title,
  count,
  totalOrder,
  totalEndurance,
  totalRefund,
  expanded,
  onClick,
  action,
}) => (
  <div onClick={onClick} className={`branchesFaults-summary-card ${className}`}>
    <div className="branchesFaults-p center">
      <p>
        <strong>{title}</strong>
      </p>
    </div>
    <div className="branchesFaults-p">
      <p>
        <strong>{count}</strong>
      </p>
    </div>
    <div className="branchesFaults-p">
      <p>
        <strong>{formatter.format(totalOrder)}</strong>
      </p>
    </div>
    <div className="branchesFaults-p">
      <p>
        <strong>{formatter.format(totalEndurance)}</strong>
      </p>
    </div>
    <div className="branchesFaults-p">
      <p>
        <strong>{formatter.format(totalRefund)}</strong>
      </p>
    </div>
    {action}
    {typeof expanded === "boolean" && !action && (
      <div className="branchesFaults-card-icon" aria-hidden="true">
        {expanded ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
      </div>
    )}
  </div>
);

export const BranchesFaults = () => {
  const { lang, storeAccess, branchAccess, handleTokenExpiration } =
    useContext(Context);

  const [details, setDetails] = useState([]);
  const [objections, setObjections] = useState([]);
  const [searchText, setSearchText] = useState({});
  const [checkboxFilters, setCheckboxFilters] = useState({});
  const [expandedGroup, setExpandedGroup] = useState(null);

  const maps = useMemo(
    () => ({
      orderStatus: translations[lang].filter.orderStatus,
      app: translations[lang].filter.apps,
    }),
    [lang],
  );

  const checkboxFields = useMemo(
    () => [
      COMP_FIELDS2.app,
      COMP_FIELDS2.storeName,
      COMP_FIELDS2.branchName,
      COMP_FIELDS2.orderStatus,
      COMP_FIELDS2.cause,
    ],
    [],
  );

  const fetchDetails = async () => {
    try {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        handleTokenExpiration();
        return;
      }

      const data = await fetchBranchesCompsAPI(
        token,
        storeAccess,
        branchAccess,
      );
      if (data) setDetails(data);
    } catch (error) {
      console.error("Fetching failed details:", error);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchObjections = async () => {
    try {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        handleTokenExpiration();
        return;
      }

      const data = await getObjectionsIdsAPI(token, storeAccess, branchAccess);
      if (data) setObjections(data);
    } catch (error) {
      console.error("Fetching failed details:", error);
    }
  };

  useEffect(() => {
    fetchObjections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const objectionSet = useMemo(
    () => new Set(objections.map(String)),
    [objections],
  );

  const uniqueOptionsByField = useMemo(() => {
    const options = {};
    const seen = {};

    checkboxFields.forEach((field) => {
      options[field] = [];
      seen[field] = new Set();
    });

    details.forEach((item) => {
      checkboxFields.forEach((field) => {
        const value = item[field];
        if (!value) return;

        const key = normalize(value);
        if (seen[field].has(key)) return;

        seen[field].add(key);
        options[field].push(value);
      });
    });

    return options;
  }, [details, checkboxFields]);

  const branchOptions = useMemo(() => {
    const selectedStores = checkboxFilters[COMP_FIELDS2.storeName] || [];
    if (!selectedStores.length)
      return uniqueOptionsByField[COMP_FIELDS2.branchName] || [];

    const selectedStoreSet = new Set(selectedStores.map(normalize));
    const branchSet = new Set();
    const branches = [];

    details.forEach((item) => {
      const store = normalize(item[COMP_FIELDS2.storeName]);
      const branch = item[COMP_FIELDS2.branchName];
      if (!branch || !selectedStoreSet.has(store)) return;

      const branchKey = normalize(branch);
      if (branchSet.has(branchKey)) return;

      branchSet.add(branchKey);
      branches.push(branch);
    });

    return branches;
  }, [details, checkboxFilters, uniqueOptionsByField]);

  const filteredDetails = useMemo(() => {
    const [selectedYear, selectedMonthNum] = (searchText.date || "").split("-");
    const selectedMonthName = selectedMonthNum
      ? monthNames[selectedMonthNum]
      : null;

    const normalizedFilters = checkboxFields.reduce((acc, field) => {
      const active = checkboxFilters[field] || [];
      acc[field] = new Set(active.map(normalize));
      return acc;
    }, {});

    return details.filter((item) => {
      if (selectedMonthName) {
        const itemDate = item[COMP_FIELDS2.date] || "";
        const [, itemMonthRaw, itemYearRaw] = itemDate.split("-");

        if (
          itemMonthRaw !== selectedMonthName ||
          itemYearRaw !== selectedYear
        ) {
          return false;
        }
      }

      for (const field of checkboxFields) {
        const active = normalizedFilters[field];
        if (active?.size && !active.has(normalize(item[field]))) return false;
      }

      return true;
    });
  }, [details, searchText.date, checkboxFilters, checkboxFields]);

  const storesSummary = useMemo(() => {
    const grouped = new Map();

    filteredDetails.forEach((item) => {
      const store = item[COMP_FIELDS2.storeName] || "-";
      const orderId = String(item[COMP_FIELDS2.orderId] || item.orderId || "");

      if (!grouped.has(store)) {
        grouped.set(store, {
          store,
          count: 0,
          objectedCount: 0,
          totalOrder: 0,
          totalEndurance: 0,
          totalRefund: 0,
          rows: [],
        });
      }

      const summary = grouped.get(store);
      const refundAmount = toNumber(item[COMP_FIELDS2.refundAmount]);

      summary.count += 1;
      summary.rows.push(item);
      summary.totalOrder += toNumber(item[COMP_FIELDS2.orderAmount]);
      summary.totalEndurance += toNumber(item[COMP_FIELDS2.enduranceAmount]);
      summary.totalRefund += refundAmount;

      if (objectionSet.has(orderId) || refundAmount > 0) {
        summary.objectedCount += 1;
      }
    });

    return Array.from(grouped.values());
  }, [filteredDetails, objectionSet]);

  const handleFilterChange = (field, values) => {
    setCheckboxFilters((prev) => ({
      ...prev,
      [field]: values,
      ...(field === COMP_FIELDS2.storeName ? { [COMP_FIELDS2.branchName]: [] } : {}),
    }));
    setExpandedGroup(null);
  };

  const handleClearFilters = () => {
    setSearchText({ date: "" });
    setCheckboxFilters({});
    setExpandedGroup(null);
  };

  const getOptionLabel = (field, value) => {
    if (field === COMP_FIELDS2.app) {
      return (
        maps[field]?.[value] ??
        `${translations[lang].notApp} ${String(value).slice(-1)}`
      );
    }

    return maps[field]?.[value] ?? value;
  };

  return (
    <div className="branchesFaults-table">
      <div className="branchesFaults-search-panel">
        <div className="branchesFaults-text-filter">
          <Input
            type="month"
            label={translations[lang].reports.details.inputTitles[0]}
            value={searchText.date || ""}
            onChange={(e) => {
              setSearchText((prev) => ({ ...prev, date: e.target.value }));
              setExpandedGroup(null);
            }}
          />

          {checkboxFields.map((field, i) => {
            const options =
              field === COMP_FIELDS2.branchName
                ? branchOptions
                : uniqueOptionsByField[field] || [];

            return (
              <FilterGroup
                key={field}
                label={translations[lang].reports.details.inputTitles[i + 1]}
                options={options}
                selected={checkboxFilters[field] || []}
                onChange={(values) => handleFilterChange(field, values)}
                getLabel={(value) => getOptionLabel(field, value)}
                getValue={(value) => value}
                fullWidth={checkboxFields.length - 1 === i ? "full-width" : ""}
              />
            );
          })}
        </div>

        <Button
          variant="warning"
          appearance="soft"
          onClick={handleClearFilters}
        >
          {translations[lang].reports.details.inputTitles[6]}
        </Button>
      </div>

      <div className="branchesFaults-card-col">
        <div className="branchesFaults-header-card">
          {translations[lang].reports.details.header.map((item, i) => (
            <p key={i}>
              <strong>{item}</strong>
            </p>
          ))}
        </div>

        {storesSummary.map((summary) => {
          const isExpanded = expandedGroup === summary.store;

          return (
            <React.Fragment key={summary.store}>
              <FaultSummaryCard
                expanded={isExpanded}
                onClick={() =>
                  setExpandedGroup(isExpanded ? null : summary.store)
                }
                className={isExpanded ? "colored" : ""}
                title={summary.store}
                count={`${summary.objectedCount}/${summary.count}`}
                totalOrder={summary.totalOrder}
                totalEndurance={summary.totalEndurance}
                totalRefund={summary.totalRefund}
              />

              {isExpanded && (
                <ExpandBranchesFaults
                  objections={objections}
                  data={summary.rows}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
